import type { FeedbackKey, Puzzle } from "../puzzle/types.ts";
import { selectGlyph, type GlyphManifest, type PaperVariant } from "./glyphs.ts";
import { glyphFrames, mobilePaperAspect, paperRuling, ruleBaseline } from "./notebook-layout.ts";

const TITLE_LEFT = 330;
const TITLE_WIDTH = 830;

interface NotebookInkOptions {
  glyphs: GlyphManifest;
  paper: PaperVariant;
  puzzle: Puzzle;
  result?: { solved: boolean; value: string };
}

const UNDERLINES = [
  "M2 6 C24 2 62 8 98 4",
  "M2 4 C31 8 68 1 98 6",
  "M2 6 Q48 1 98 5",
];

const CLUE_INKS = {
  count: "--cp-ink",
  right: "--cp-clue-right",
  wrong: "--cp-clue-wrong",
  none: "--cp-clue-none",
} as const;

type ClueInk = keyof typeof CLUE_INKS;

const FEEDBACK_INKS: Record<FeedbackKey, readonly ClueInk[]> = {
  none: ["none"],
  oneWell: ["count", "right"],
  oneWrong: ["count", "wrong"],
  twoWell: ["count", "right"],
  twoWrong: ["count", "wrong"],
  oneWellOneWrong: ["right", "wrong"],
};

function attribute(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
}

function number(value: number): string {
  return value.toFixed(3);
}

export function notebookInk({ glyphs, paper, puzzle, result }: NotebookInkOptions): string {
  const ruling = paperRuling(paper);

  const renderSize = (mobile: boolean): string => {
    const verticalScale = mobile ? mobilePaperAspect(puzzle.clues.length) / (14 / 19) : 1;
    const filterId = `notebook-ink-${mobile ? "mobile" : "desktop"}`;
    const draw = (
      key: string,
      instance: number,
      left: number,
      row: number,
      fontSize: number,
      maxWidth: number,
      className = "",
      inks: readonly ClueInk[] = [],
    ): string => {
      const selected = selectGlyph(glyphs, key, puzzle.seed, instance);
      if (!selected) return "";
      const frames = glyphFrames(selected, paper, { x: left, row, fontSize, maxWidth, verticalScale });
      return frames.map((frame, lineIndex) => {
        const ink = inks[lineIndex] ?? (className.includes("reveal-glyph") ? "reveal" : "");
        return `
        <svg class="ruled-glyph ${className}" data-glyph="${key}" data-line="${lineIndex}"
          data-baseline="${number(frame.baseline)}" data-baseline-offset="${number(frame.baselineOffset)}"
          x="${number(frame.x)}" y="${number(frame.y)}" width="${number(frame.width)}" height="${number(frame.height)}"
          viewBox="0 ${number(frame.sourceTop)} ${selected.width} ${number(frame.sourceHeight)}"
          preserveAspectRatio="none">
          <image href="${attribute(selected.src)}" width="${selected.width}" height="${selected.height}"
            filter="url(#${filterId}${ink ? `-${ink}` : ""})" />
        </svg>`;
      }).join("");
    };

    // The handwritten title is centred in the heading column so short and long names both sit well.
    const titleSize = mobile ? 170 : 136;
    const titleGlyph = selectGlyph(glyphs, "title", puzzle.seed, 0);
    const titleFrame = titleGlyph ? glyphFrames(titleGlyph, paper, { x: 0, row: 0, fontSize: titleSize, maxWidth: TITLE_WIDTH, verticalScale })[0] : undefined;
    const titleLeft = TITLE_LEFT + Math.max(0, (TITLE_WIDTH - (titleFrame?.width ?? TITLE_WIDTH)) / 2);
    const taglineSize = mobile ? 92 : 70;
    const taglineGlyph = selectGlyph(glyphs, "tagline", puzzle.seed, 0);
    const taglineFrame = taglineGlyph ? glyphFrames(taglineGlyph, paper, { x: 0, row: 1, fontSize: taglineSize, maxWidth: TITLE_WIDTH, verticalScale })[0] : undefined;
    const taglineLeft = TITLE_LEFT + Math.max(0, (TITLE_WIDTH - (taglineFrame?.width ?? TITLE_WIDTH)) / 2);

    const drawNumber = (value: string, left: number, row: number, fontSize: number, step: number, instance: number, className = ""): string =>
      [...value].map((digit, index) => draw(
        digit === ":" ? "punctuation-colon" : `digit-${digit}`,
        instance + index,
        left + index * step,
        row,
        fontSize,
        step * 1.1,
        className,
      )).join("");

    const clues = puzzle.clues.map((clue, index) => {
      const left = mobile ? 230 : (index % 2 === 0 ? 270 : 800) + ((puzzle.seed + index * 13) % 17 - 8);
      const row = mobile
        ? (puzzle.clues.length > 4 ? 6 + index * 2 : 6 + index * 3)
        : puzzle.clues.length > 4 ? 6 + Math.floor(index / 2) * 4 : 7 + Math.floor(index / 2) * 5;
      const feedbackX = mobile ? 550 : left;
      const underline = UNDERLINES[index % UNDERLINES.length] ?? UNDERLINES[0];
      return `<g class="visual-clue" data-clue="${index}">
        <g class="clue-guess">${drawNumber(clue.guess.join(""), left, row, mobile ? 160 : 136, mobile ? 87 : 86, index * 3 + 20)}</g>
        <svg class="clue-underline" x="${left + 12}" y="${ruleBaseline(paper, row) + 12}" width="240" height="12"
          viewBox="0 0 100 10" preserveAspectRatio="none"><path d="${underline}" /></svg>
        <g class="clue-feedback">${draw(`feedback-${clue.feedback.key}`, index, feedbackX, mobile ? row : row + 1, mobile ? 110 : 62, mobile ? 760 : 480, "", FEEDBACK_INKS[clue.feedback.key])}</g>
      </g>`;
    }).join("");

    const resultNote = result
      ? `<g class="paper-result ${result.solved ? "" : "paper-result-reveal"}">
          ${draw(result.solved ? "label-solved-in" : "label-code-was", 0, mobile ? 310 : 460, 20, mobile ? 104 : 72, 350)}
          ${drawNumber(result.value, mobile ? 700 : 760, 20, mobile ? 118 : 94, mobile ? 65 : 51, 240, result.solved ? "" : "reveal-glyph")}
        </g>`
      : "";

    return `<svg class="ruled-ink ${mobile ? "ruled-ink-mobile" : "ruled-ink-desktop"}"
      viewBox="0 0 ${paper.width} ${paper.height}" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <filter id="${filterId}" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
          <feFlood flood-color="currentColor" />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
        <filter id="${filterId}-reveal" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
          <feFlood flood-color="var(--cp-reveal-ink)" />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
        ${Object.entries(CLUE_INKS).map(([ink, token]) => `<filter id="${filterId}-${ink}" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
          <feFlood flood-color="var(${token})" />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>`).join("")}
      </defs>
      <g transform="rotate(${ruling.rotation} ${paper.width / 2} ${paper.height / 2})">
        <g class="title-mark">${draw("title", 0, titleLeft, 0, titleSize, TITLE_WIDTH)}</g>
        <g class="tagline-mark">${draw("tagline", 0, taglineLeft, 1, taglineSize, TITLE_WIDTH)}</g>
        <g class="code-label">${draw("label-code", 0, 225, 3, mobile ? 88 : 74, 180)}</g>
        <g class="clues-layer">${clues}</g>
        ${resultNote}
      </g>
    </svg>`;
  };

  return renderSize(false) + renderSize(true);
}